import { Box, Typography, Divider } from '@mui/material';
import { OATypography } from '../../../../components/widgets';
import { theme } from '../../../../config/theme';
import { getFormattedDate } from '../../../../helper';
import OrderInvoice from '../../OrderInvoice';
import { getSupplierRefundTable } from '../PaymentSummaryTables/supplierTables';
import { IHotelData, IRoomPriceBreakup } from '../../type';

interface ISupplierProps {
  data: IHotelData;
  isLoading: boolean;
}

interface IRoomSummary {
  taxType: string;
  roomRate: number;
  roomTax: number;
  agentCommission: number;
  taxAmount: number;
  serviceFee: number;
}

const PaymentLineItem = ({ label, amount, isTotal = false, isNegative = false }: { 
  label: string; 
  amount: number | string; 
  isTotal?: boolean;
  isNegative?: boolean;
}) => (
  <>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 8px'
      }}>
      <Typography variant={isTotal ? 'subtitle2' : 'body2'}>
        {label}
      </Typography>
      <Typography 
        variant={isTotal ? 'subtitle2' : 'body2'}
        sx={isNegative ? { color: 'success.main' } : undefined}
      >
        {isNegative ? '- ' : ''}INR {typeof amount === 'number' ? amount.toFixed(2) : amount}
      </Typography>
    </Box>
    <Divider />
  </>
);

const getTotalRoomSummary = (rooms: IRoomPriceBreakup[]): IRoomSummary => {
  return rooms.reduce(
    (totals: IRoomSummary, room: IRoomPriceBreakup) => {
      totals.roomRate += room?.roomRate ?? 0;
      totals.roomTax += room?.roomTax ?? 0;
      totals.agentCommission += room?.agentCommission ?? 0;
      totals.serviceFee += room?.serviceFee ?? 0;
      totals.taxType = room?.taxBreakup?.[0]?.taxType ?? "";
      
      if (room.taxBreakup && Array.isArray(room.taxBreakup)) {
        room.taxBreakup.forEach((tax) => {
          totals.taxAmount += tax?.taxAmount ?? 0;
        });
      }
      
      return totals;
    },
    { taxType: "", roomRate: 0, roomTax: 0, agentCommission: 0, taxAmount: 0, serviceFee: 0 }
  );
};

export default function Supplier({ data, isLoading }: ISupplierProps) {
  const supplierRefundTableData = getSupplierRefundTable({ data });
  const total = getTotalRoomSummary(data?.rooms?.flatMap(room => room.priceBreakUp) ?? []);

  const totalAmount = data?.orderAmount?.supplierAmount ?? 0;

  return (
    <Box>
      <div className='card-body py-0'>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 8px'
          }}>
          <Typography variant='subtitle2' sx={{ fontSize: '12px', lineHeight: '16px', color: 'grey.400' }}>
            PRODUCT
          </Typography>
          <Typography variant='subtitle2' sx={{ fontSize: '12px', lineHeight: '16px', color: 'grey.400' }}>
            AMOUNT
          </Typography>
        </Box>
        <Divider />

        <PaymentLineItem label="Room Rate" amount={total.roomRate} />
        
        {total.roomTax > 0 && (
          <PaymentLineItem label="Room Tax" amount={total.roomTax} />
        )}
        
        {(total.taxType === "Tax_IGST" || total.serviceFee > 0) && (
          <PaymentLineItem label="Service Fee" amount={total.serviceFee} />
        )}
        
        {total.agentCommission > 0 && (
          <PaymentLineItem label="Agent Commission" amount={total.agentCommission} isNegative />
        )}
        
        {total.taxAmount > 0 && total.agentCommission == 0 && (
          <PaymentLineItem label="Tax Amount" amount={total.taxAmount} />
        )}
        
        <PaymentLineItem 
          label="Total(Amount Paid to TBO)" 
          amount={totalAmount} 
          isTotal 
        />
      </div>

      {data?.orderAmount?.refundInfo && data.orderAmount.refundInfo.length > 0 && (
        <Box sx={{ padding: '16px' }}>
          <OATypography variant='subtitle2'>Refund details</OATypography>
          <Box display='flex' alignItems='center' marginLeft='16px' gap='71px' marginTop='24px'>
            <Box>
              <OATypography
                variant='subtitle2'
                sx={{
                  fontSize: '10px',
                  lineHeight: '16px',
                  color: theme.palette.grey[400],
                  textTransform: 'uppercase'
                }}>
                Refund date
              </OATypography>
              <OATypography variant='body2'>
                {data.orderAmount.refundInfo?.[0]?.refundDate
                  ? getFormattedDate(data.orderAmount.refundInfo?.[0].refundDate, 'DD MMM, YYYY')
                  : 'N/A'}
              </OATypography>
            </Box>
            <Box>
              <OATypography
                variant='subtitle2'
                sx={{
                  fontSize: '10px',
                  lineHeight: '16px',
                  color: theme.palette.grey[400],
                  textTransform: 'uppercase'
                }}>
                Refund Id
              </OATypography>
              <OATypography variant='body2'>
                {data.orderAmount.paymentSummary?.refundId ?? 'N/A'}
              </OATypography>
            </Box>
          </Box>
          <OrderInvoice tableData={supplierRefundTableData ?? []} loading={isLoading} />
        </Box>
      )}
    </Box>
  );
}
